import { useEffect, useState } from 'react'
import { observeCommentRepliesLive } from '@domain/features/activity-comments/activity-comments-feature'
import type { CommentReply } from '@domain/models/activity-comments'

interface UseCommentRepliesOptions {
  descending?: boolean
  limitCount?: number
  enabled?: boolean
}

export function useCommentReplies(
  commentId: string | undefined,
  options?: UseCommentRepliesOptions,
) {
  const key =
    commentId && options?.enabled !== false
      ? `${commentId}:${options?.descending === false ? 'asc' : 'desc'}:${options?.limitCount ?? 'all'}`
      : null

  const [state, setState] = useState<{
    key: string | null
    replies: CommentReply[]
    hasLoaded: boolean
    error: string | null
  }>({
    key: null,
    replies: [],
    hasLoaded: false,
    error: null,
  })

  useEffect(() => {
    if (!commentId || !key) {
      return
    }

    const unsubscribe = observeCommentRepliesLive(
      commentId,
      (nextReplies) => {
        setState({
          key,
          replies: nextReplies,
          hasLoaded: true,
          error: null,
        })
      },
      (nextError) => {
        setState((previousState) => ({
          ...previousState,
          key,
          hasLoaded: true,
          error: nextError.message,
        }))
      },
      {
        descending: options?.descending,
        limitCount: options?.limitCount,
      },
    )

    return unsubscribe
  }, [commentId, key, options?.descending, options?.limitCount])

  if (!key) {
    return {
      replies: [],
      count: 0,
      loading: false,
      error: null,
    }
  }

  const isCurrentState = state.key === key

  return {
    replies: isCurrentState ? state.replies : [],
    count: isCurrentState ? state.replies.length : 0,
    loading: isCurrentState ? !state.hasLoaded : true,
    error: isCurrentState ? state.error : null,
  }
}
